(function () {
  'use strict';

  const pg = window.__pg || { loggedIn: false, purchased: [], names: {} };
  const purchased = pg.purchased;   // product IDs the customer has bought
  const names = pg.names;           // productId -> title

  // Detect login state from Liquid flag OR DOM fallbacks (logout link, body class)
  const isLoggedIn = pg.loggedIn
    || !!document.querySelector('a[href*="/account/logout"]')
    || document.body?.classList.contains('customer-logged-in')
    || purchased.length > 0;

  const _fetch = window.fetch.bind(window);

  // ── Modal ──────────────────────────────────────────────────────────────────

  function modal(title, body, buttons) {
    document.getElementById('sp-guard')?.remove();

    const overlay = Object.assign(document.createElement('div'), { id: 'sp-guard' });
    overlay.style.cssText = [
      'position:fixed;inset:0;z-index:99999',
      'background:rgba(30,30,30,.6)',
      'display:flex;align-items:center;justify-content:center',
      'padding:20px;box-sizing:border-box',
      'animation:spFadeIn .18s ease',
    ].join(';');

    // Inject keyframe once
    if (!document.getElementById('sp-guard-style')) {
      const style = document.createElement('style');
      style.id = 'sp-guard-style';
      style.textContent = `
        @keyframes spFadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes spSlideUp { from { opacity:0;transform:translateY(12px) } to { opacity:1;transform:translateY(0) } }
        #sp-guard button:hover { opacity:.85; }
      `;
      document.head.appendChild(style);
    }

    const card = document.createElement('div');
    card.style.cssText = [
      'background:#fff;border-radius:8px',
      'padding:44px 40px 36px',
      'max-width:460px;width:100%',
      'box-shadow:0 12px 40px rgba(0,0,0,.22)',
      'text-align:center;font-family:inherit',
      'animation:spSlideUp .22s ease',
      'position:relative',
    ].join(';');

    // Icon
    const icon = document.createElement('div');
    icon.style.cssText = 'margin:0 auto 18px;width:48px;height:48px';
    icon.innerHTML = `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="24" r="24" fill="#f5f0eb"/>
      <path d="M16 24l6 6 10-12" stroke="#1a1a1a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;

    const h = document.createElement('h2');
    h.innerHTML = title;
    h.style.cssText = 'margin:0 0 12px;font-size:1.1rem;font-weight:600;color:#1a1a1a;line-height:1.35';

    const p = document.createElement('p');
    p.innerHTML = body;
    p.style.cssText = 'margin:0 0 30px;color:#666;font-size:.9rem;line-height:1.65';

    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:10px;justify-content:center;flex-wrap:wrap';

    buttons.forEach(({ label, primary, click }) => {
      const btn = document.createElement('button');
      btn.textContent = label;
      btn.style.cssText = primary
        ? 'padding:12px 28px;background:#1a1a1a;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:.875rem;font-weight:500;letter-spacing:.02em;transition:opacity .15s'
        : 'padding:12px 28px;background:#fff;color:#666;border:1px solid #d5d5d5;border-radius:4px;cursor:pointer;font-size:.875rem;transition:opacity .15s';
      btn.onclick = () => { overlay.remove(); click?.(); };
      row.appendChild(btn);
    });

    overlay.onclick = e => { if (e.target === overlay) overlay.remove(); };

    card.append(icon, h, p, row);
    overlay.appendChild(card);
    document.body.appendChild(overlay);
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  function alreadyPurchased(productId) {
    return purchased.includes(productId);
  }

  function productName(productId, fallback) {
    return names[productId] || fallback || 'this pattern';
  }

  function showDuplicateModal(productId, fallbackTitle) {
    modal(
      'You already own this pattern',
      `Your account shows you've already purchased <strong>${productName(productId, fallbackTitle)}</strong>.<br><br>Each pattern is a single purchase — you can download it anytime from your account.`,
      [
        { label: 'Go to my account', primary: true, click: () => { window.location.href = '/account'; } },
        { label: 'Got it', primary: false },
      ]
    );
  }

  // ── Trigger 1: Add to Cart ─────────────────────────────────────────────────
  // Strategy: let the add go through, read the response (product_id is in there),
  // immediately remove if duplicate, then show modal. Fires at cart drawer open time.

  // Form submit — quantity enforcement only (capture phase = runs before theme JS)
  document.addEventListener('submit', function (e) {
    const form = e.target;
    if (!form.action?.includes('/cart/add')) return;
    const qty = form.querySelector('[name="quantity"]');
    if (qty && parseInt(qty.value, 10) > 1) qty.value = 1;
  }, true);

  // Fetch intercept — quantity + duplicate check via response
  window.fetch = async function (input, init = {}) {
    const url = typeof input === 'string' ? input
      : input instanceof URL ? input.href
      : input?.url ?? '';

    if (url.includes('/cart/add')) {
      // Enforce quantity = 1 in JSON body before sending
      if (typeof init.body === 'string') {
        try {
          const data = JSON.parse(init.body);
          if (data.quantity > 1) data.quantity = 1;
          init = { ...init, body: JSON.stringify(data) };
        } catch (_) {}
      }

      const response = await _fetch(input, init);

      // Read response to get the product that was just added
      if (response.ok && purchased.length) {
        try {
          const added = await response.clone().json();
          // /cart/add.js returns the line item; product_id is on it directly
          const productId = added.product_id;
          if (productId && alreadyPurchased(productId)) {
            // Remove immediately before the cart drawer finishes rendering
            await _fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ updates: { [added.id]: 0 } }),
            });
            showDuplicateModal(productId, added.product_title);
          }
        } catch (_) {}
      }

      return response;
    }

    return _fetch(input, init);
  };

  // ── Trigger 2: Page load — check cart after login ─────────────────────────

  // Returns true if duplicates were found (modal shown), false if cart is clean
  async function auditCart() {
    if (!purchased.length) return false;

    let cart;
    try {
      const res = await _fetch('/cart.js');
      cart = await res.json();
    } catch (_) { return false; }

    if (!cart.items?.length) return false;

    // Silently fix any quantities > 1
    const quantityFixes = {};
    cart.items.forEach(item => {
      if (item.quantity > 1) quantityFixes[item.id] = 1;
    });
    if (Object.keys(quantityFixes).length) {
      try {
        await _fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: quantityFixes }),
        });
      } catch (_) {}
    }

    // Find already-purchased items (skip gift cards)
    const dupes = cart.items.filter(item =>
      item.product_type !== 'Gift Card' &&
      item.product_type !== 'Gift Cards' &&
      alreadyPurchased(item.product_id)
    );

    if (!dupes.length) return false;

    const list = dupes
      .map(d => `<li style="margin:5px 0"><strong>${d.title}</strong></li>`)
      .join('');

    modal(
      'You already own some items in your cart',
      `These patterns are already in your account:<ul style="text-align:left;padding-left:22px;margin:14px 0 0;color:#444">${list}</ul>`,
      [
        {
          label: 'Remove them',
          primary: true,
          click: async () => {
            const removes = {};
            dupes.forEach(d => { removes[d.id] = 0; });
            try {
              await _fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates: removes }),
              });
              sessionStorage.removeItem('sp_pending_checkout');
              window.location.href = '/cart';
            } catch (_) {}
          },
        },
        { label: 'Keep them', primary: false },
      ]
    );

    return true;
  }

  // ── Checkout button guard ──────────────────────────────────────────────────
  // Uses event delegation (capture) to catch dynamically-rendered cart drawer buttons.
  // Sets a sessionStorage flag so auditCart() knows to redirect to checkout
  // after login, regardless of where Shopify lands the customer.

  document.addEventListener('click', function (e) {
    const btn = e.target.closest('a[href*="/checkout"], button[name="checkout"], input[name="checkout"]');
    if (!btn) return;
    if (isLoggedIn) return; // logged in — let checkout proceed normally

    e.preventDefault();
    e.stopImmediatePropagation();
    sessionStorage.setItem('sp_pending_checkout', Date.now().toString());
    window.location.href = '/account/login?return_to=%2Fcart';
  }, true);

  // ── On page load: audit + handle post-login checkout redirect ─────────────

  function getPendingCheckout() {
    const ts = parseInt(sessionStorage.getItem('sp_pending_checkout'));
    if (!ts) return false;
    // Expire after 30 minutes — clears stale flags without touching them on the login page
    if (Date.now() - ts > 30 * 60 * 1000) {
      sessionStorage.removeItem('sp_pending_checkout');
      return false;
    }
    return true;
  }

  document.addEventListener('DOMContentLoaded', async () => {
    const pendingCheckout = getPendingCheckout();
    const hadIssues = await auditCart();

    if (pendingCheckout && isLoggedIn && !hadIssues) {
      sessionStorage.removeItem('sp_pending_checkout');
      window.location.href = '/checkout';
    }
    // No else — never clear the flag while the customer isn't logged in yet
  });
})();
